import React from "react";
import type { NewsletterDTO } from "@/generated-client";
import { Button } from "@/components/ui/button";
import { ClientFactory } from "@/main/common/ClientFactory";
import { Localizer } from "@/main/react/Types";
import { NewsletterSubscribeControl, type NewsletterSubscribeSurface } from "@/main/react/newsletter/NewsletterSubscribeControl";
import { newsletterPageUrl } from "@/main/react/newsletter/newsletterUrls";

/**
 * The reader's "Get {name} by email" line (newsletter growth work order D12/D13, slice 2c): the sentence
 * plus the one control that acts on it. A member subscribes in one tap through `NewsletterSubscribeControl`
 * (which already reads "Subscribed" for a subscriber); a visitor gets a link to the newsletter page, where
 * the double opt-in form lives. The OWNER never gets this line: callers decide that, because only they know
 * who is looking (the post page knows its author, the profile knows its owner).
 */
export type NewsletterGetByEmailLineProps = {
    newsletter: Pick<NewsletterDTO, "publicId" | "name" | "slug" | "ownerUsername" | "viewerSubscribed">;
    localizer: Localizer;
    loggedIn: boolean;
    surface: NewsletterSubscribeSurface;
    className?: string;
};

export function NewsletterGetByEmailLine({ newsletter, localizer, loggedIn, surface, className }: NewsletterGetByEmailLineProps) {
    const pageUrl = newsletterPageUrl(newsletter.slug);
    return (
        <div
            className={className ?? "flex flex-wrap items-center justify-between gap-3"}
            data-playwright-id="newsletter-get-by-email"
            data-newsletter-public-id={newsletter.publicId}
            data-surface={surface}
        >
            <p className="min-w-0 text-sm font-medium text-foreground">
                {localizer("newsletter.subscribe.get_by_email", newsletter.name)}
            </p>
            {loggedIn ? (
                <NewsletterSubscribeControl
                    publicId={newsletter.publicId}
                    ownerUsername={newsletter.ownerUsername ?? undefined}
                    initiallySubscribed={Boolean(newsletter.viewerSubscribed)}
                    localizer={localizer}
                    loggedIn={true}
                    surface={surface}
                />
            ) : pageUrl ? (
                <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="touch-manipulation [@media(pointer:coarse)]:min-h-11"
                >
                    <a href={pageUrl} data-playwright-id="newsletter-get-by-email-link">
                        {localizer("newsletter.subscribe")}
                    </a>
                </Button>
            ) : null}
        </div>
    );
}

/**
 * The author's newsletter to offer under a post (A13): the most-subscribed of their public ones. A count the
 * server withholds (A16: null for a non-owner below the public threshold) ranks as zero, which is what it is
 * below the threshold anyway. Ties keep the server's order.
 */
export function pickMostSubscribedNewsletter<T extends Pick<NewsletterDTO, "subscribersCount">>(newsletters: readonly T[] | null | undefined): T | null {
    if (!newsletters || newsletters.length === 0) {
        return null;
    }
    const countOf = (newsletter: T) => (typeof newsletter.subscribersCount === "number" ? newsletter.subscribersCount : 0);
    return newsletters.reduce((best, candidate) => (countOf(candidate) > countOf(best) ? candidate : best), newsletters[0]);
}

/**
 * `GET /ajax/newsletter/by-user/{username}` (A12): the member's ACTIVE + PUBLIC newsletters with the viewer's
 * own subscribed flag. Returns `null` while unanswered and on any failure: every caller treats the newsletter
 * surfaces as an offer, so a failed read renders nothing rather than an error (and nothing server-rendered
 * sits under any of them, so nothing is subtracted, rule 32). `enabled: false` issues no request at all.
 */
export function useMemberPublicNewsletters(username: string | null | undefined, enabled: boolean = true): NewsletterDTO[] | null {
    const [newsletters, setNewsletters] = React.useState<NewsletterDTO[] | null>(null);

    React.useEffect(() => {
        if (!enabled || !username) {
            return;
        }
        let cancelled = false;
        // try/catch AROUND the call as well as `.catch()`: a synchronous throw escaping this effect would
        // unmount the host island (FollowAuthorPrompt's affinity request carries the same guard, and why).
        try {
            ClientFactory.getAjaxNewsletterRestControllerApi()
                .listPublicNewslettersByUser(username)
                .then((response) => {
                    if (!cancelled) {
                        setNewsletters(Array.isArray(response?.data) ? response.data.filter(Boolean) : []);
                    }
                })
                .catch(() => {
                    // An offer, not content: a failed read simply shows nothing.
                });
        } catch {
            // As above.
        }
        return () => {
            cancelled = true;
        };
    }, [enabled, username]);

    return newsletters;
}
